import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getManagementDashboardFilterOptions } from "../../../../../infrastructure/api/managementDashboard";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import { buildCheckNewRequestModifyPath } from "../../shared/checkNewRequest/checkNewRequestPageUtils";
import { buildProjectWorkspacePath, WORKSPACE_LIST_VIEWS } from "../../shared/projectWorkspace/projectWorkspacePaths";
import EnhancedTable from "../../../../components/shared/table";
import { getCurrentUserId } from "../../../../components/request/requestAssignmentDisplay";
import { useManagementAllRequestPagedList } from "../../../../components/request/useManagementAllRequestPagedList";
import AllRequestsCaseDrawer from "../../solarent-operator/all-requests/AllRequestsCaseDrawer";
import { normalizeFilterOptions } from "../dashboard/managementDashboardUtils";
import AllRequestAppliedFiltersBar from "./AllRequestAppliedFiltersBar";
import FilteredRequestExportButton from "./FilteredRequestExportButton";
import {
  buildAppliedFilterChips,
  createDefaultDrillDownParams,
  drillDownParamsToSearchParams,
  parseAllRequestDrillDownParams,
  removeDrillDownChip,
  withPageReset,
} from "./allRequestDrillDownUtils";
import {
  columnIdToSortBy,
  DEFAULT_PAGE_SIZE,
  sortByToColumnId,
} from "./allRequestPaginationUtils";
import { enrichManagementOperationalRows } from "./managementAllRequestUtils";

export const MANAGEMENT_CASE_DETAIL_API = "SLN/Management/CheckNewRequest";
export const MANAGEMENT_CASE_HISTORY_API = "SLN/Management/CheckNewRequest";
export const MANAGEMENT_ALL_REQUEST_LIST_API = "SLN/Management/AllRequest";

const PAGE_TITLE = "همه درخواست‌ها (مدیریت)";

const OPERATIONAL_HEAD_CELLS = [
  { id: "opsOwnerLabel", label: "مسئول", type: "chip", model: { colorField: "opsOwnerColor" } },
  { id: "opsLeadPriorityLabel", label: "اولویت", type: "chip", model: { colorField: "opsLeadPriorityColor" } },
  { id: "opsFollowUpLabel", label: "پیگیری", type: "chip", model: { colorField: "opsFollowUpColor" } },
  { id: "opsMehrsanLabel", label: "مهرسان", type: "chip", model: { colorField: "opsMehrsanColor" } },
  { id: "opsLendingLabel", label: "وام", type: "chip", model: { colorField: "opsLendingColor" } },
];

const ManagementAllRequestPage = ({ hubMode = false } = {}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = useCheckPermission("edit");
  const currentUserId = getCurrentUserId();
  const [reloadToken, setReloadToken] = useState(0);

  const listParams = useMemo(
    () => parseAllRequestDrillDownParams(searchParams),
    [searchParams]
  );

  const {
    rows: dataList,
    totalCount,
    pageMeta,
    loading,
    error,
    showAssignmentUi,
    filterOptions: assignmentFilterOptions,
  } = useManagementAllRequestPagedList(
    MANAGEMENT_ALL_REQUEST_LIST_API,
    listParams,
    listParams.assignmentFilter || "all",
    reloadToken
  );

  const [searchInput, setSearchInput] = useState(listParams.searchTerm || "");
  const [caseDrawer, setCaseDrawer] = useState({ open: false, id: null, row: null });
  const [filterOptions, setFilterOptions] = useState({
    provinces: [],
    cities: [],
    projectTypes: [],
    requestStatuses: [],
    lendingPartners: [],
    contractors: [],
    registrationChannels: [],
  });

  useEffect(() => {
    const provinceId = listParams.provinceId || undefined;
    getManagementDashboardFilterOptions(provinceId)
      .then((res) => {
        if (res?.isSuccess && res.data) {
          setFilterOptions(normalizeFilterOptions(res.data));
        }
      })
      .catch(() => {});
  }, [listParams.provinceId]);

  const syncSearchParams = useCallback(
    (nextParams) => {
      const sp = drillDownParamsToSearchParams(nextParams);
      if (hubMode) {
        sp.set("view", "all");
      }
      setSearchParams(sp, { replace: true });
    },
    [hubMode, setSearchParams]
  );

  useEffect(() => {
    setSearchInput(listParams.searchTerm || "");
  }, [listParams.searchTerm]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = (searchInput || "").trim();
      if (trimmed === (listParams.searchTerm || "").trim()) return;
      syncSearchParams(
        withPageReset({
          ...listParams,
          searchTerm: trimmed,
        })
      );
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput, listParams, syncSearchParams]);

  const appliedChips = useMemo(
    () => buildAppliedFilterChips(listParams, filterOptions),
    [listParams, filterOptions]
  );

  const handleRemoveChip = useCallback(
    (chip) => {
      syncSearchParams(withPageReset(removeDrillDownChip(listParams, chip)));
    },
    [listParams, syncSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    syncSearchParams(createDefaultDrillDownParams());
  }, [syncSearchParams]);

  const handlePageChange = useCallback(
    (pageIndex) => {
      syncSearchParams({
        ...listParams,
        pageNumber: pageIndex + 1,
      });
    },
    [listParams, syncSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (pageSize) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          pageSize,
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleSortChange = useCallback(
    (columnId, order) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          sortBy: columnIdToSortBy(columnId),
          sortDirection: order,
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleAssignmentFilterChange = useCallback(
    (value) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          assignmentFilter: value,
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const handleStatusFilterChange = useCallback(
    (value) => {
      syncSearchParams(
        withPageReset({
          ...listParams,
          requestStatus: value || "",
        })
      );
    },
    [listParams, syncSearchParams]
  );

  const rows = useMemo(
    () => enrichManagementOperationalRows(dataList, currentUserId),
    [dataList, currentUserId]
  );

  const headCells = useMemo(
    () => [
      { id: "referenceID", label: "کد" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "requestConfirmTitle", label: "وضعیت" },
      { id: "stepTitle", label: "مرحله" },
      ...OPERATIONAL_HEAD_CELLS,
      { id: "updateOn", label: "بروزرسانی", type: "date" },
    ],
    []
  );

  const serverPagination = useMemo(
    () => ({
      totalCount,
      page: Math.max(0, (listParams.pageNumber || 1) - 1),
      rowsPerPage: listParams.pageSize || DEFAULT_PAGE_SIZE,
      orderBy: sortByToColumnId(listParams.sortBy),
      order: listParams.sortDirection === "asc" ? "asc" : "desc",
      onPageChange: handlePageChange,
      onRowsPerPageChange: handlePageSizeChange,
      onSortChange: handleSortChange,
    }),
    [
      totalCount,
      listParams.pageNumber,
      listParams.pageSize,
      listParams.sortBy,
      listParams.sortDirection,
      handlePageChange,
      handlePageSizeChange,
      handleSortChange,
    ]
  );

  const onOpenCase = useCallback((row) => {
    setCaseDrawer({ open: true, id: row.id, row });
  }, []);

  const onOpenWorkspace = useCallback(
    (row) => {
      const path = buildProjectWorkspacePath(row, "management", {
        listView: WORKSPACE_LIST_VIEWS.all,
      });
      if (path) navigate(path);
    },
    [navigate]
  );

  const onEditClicked = useCallback(
    (row) => {
      const path = buildCheckNewRequestModifyPath(row, "management");
      if (path) navigate(path);
    },
    [navigate]
  );

  const tableActions = useMemo(() => {
    const actions = [
      {
        icon: <FolderOpenIcon fontSize="small" />,
        onClick: onOpenWorkspace,
        color: "primary",
        tooltip: "پرونده پروژه (Workspace)",
      },
      {
        icon: <ContentPasteSearchIcon fontSize="small" />,
        onClick: onOpenCase,
        color: "info",
        tooltip: "بررسی پرونده",
      },
    ];

    if (canEdit) {
      actions.splice(1, 0, {
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: onEditClicked,
        color: "secondary",
        tooltip: "ویرایش درخواست",
      });
    }

    return actions;
  }, [canEdit, onOpenCase, onEditClicked, onOpenWorkspace]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {!hubMode ? <BreadcrumbsComponent /> : null}
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="صفحه‌بندی سمت سرور — فیلترهای داشبورد از طریق URL اعمال می‌شوند"
            action={
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <FilteredRequestExportButton listParams={listParams} disabled={loading} />
                <IconButton
                  color="primary"
                  onClick={() => setReloadToken((t) => t + 1)}
                  aria-label="بروزرسانی لیست"
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />

          <CardContent>
            <AllRequestAppliedFiltersBar
              chips={appliedChips}
              fromDashboard={Boolean(listParams.fromDashboard)}
              onRemoveChip={handleRemoveChip}
              onClearAll={handleClearFilters}
            />

            {error ? (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setReloadToken((t) => t + 1)}>
                    تلاش مجدد
                  </Button>
                }
              >
                {error}
              </Alert>
            ) : null}

            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="جستجو در کل نتایج (کد، نام، موبایل، کد ملی...)"
                sx={{ flex: "1 1 240px", minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Chip
                size="small"
                variant="outlined"
                label={`${totalCount.toLocaleString("fa-IR")} نتیجه`}
                sx={{ flexShrink: 0 }}
              />

              {showAssignmentUi ? (
                <FormControl size="small" sx={{ flex: "0 0 170px", minWidth: 150 }}>
                  <InputLabel>فیلتر مسئولیت</InputLabel>
                  <Select
                    value={listParams.assignmentFilter || "all"}
                    label="فیلتر مسئولیت"
                    onChange={(e) => handleAssignmentFilterChange(e.target.value)}
                  >
                    {assignmentFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}

              <FormControl size="small" sx={{ flex: "0 0 170px", minWidth: 150 }}>
                <InputLabel>وضعیت</InputLabel>
                <Select
                  value={listParams.requestStatus || ""}
                  label="وضعیت"
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {(filterOptions.requestStatuses || []).map((opt) => (
                    <MenuItem key={opt.value} value={String(opt.value)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <EnhancedTable
              rowKey="id"
              rows={rows}
              headCells={headCells}
              defaultSort={{
                orderBy: sortByToColumnId(listParams.sortBy),
                order: listParams.sortDirection === "asc" ? "asc" : "desc",
              }}
              pageSize={listParams.pageSize || DEFAULT_PAGE_SIZE}
              showFilter={false}
              loading={loading}
              tableMinWidth={960}
              actionsMode="menu"
              actions={tableActions}
              serverPagination={serverPagination}
            />

            {!loading && totalCount > 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                صفحه {pageMeta.pageNumber.toLocaleString("fa-IR")} از{" "}
                {Math.max(pageMeta.totalPages, 1).toLocaleString("fa-IR")}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      </Grid>

      <AllRequestsCaseDrawer
        open={caseDrawer.open}
        requestId={caseDrawer.id}
        listRow={caseDrawer.row}
        onClose={() => setCaseDrawer({ open: false, id: null, row: null })}
        detailApi={MANAGEMENT_CASE_DETAIL_API}
        historyApi={MANAGEMENT_CASE_HISTORY_API}
        workspaceConfigKey="management"
      />
    </Grid>
  );
};

export default ManagementAllRequestPage;
